/**
 * Bilan complet 360° d'un étudiant — vue synthétique pour la scolarité.
 *
 * Onglets : Vue d'ensemble · Résultats · Assiduité · Conduite · Santé ·
 *           Communication · Rachats/Rattrapages · Paiement
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Phone, Mail, Calendar, MapPin, Hash,
  GraduationCap, TrendingUp, TrendingDown, AlertTriangle,
  Award, CheckCircle2, XCircle, Clock, MessageSquare,
  FileText, Shield, Heart, CreditCard, Loader2, Printer,
  Edit, RefreshCw, ChevronRight, Star,
} from 'lucide-react';
import { useGetBilanQuery } from '../../services/bilanEtudiantApi';
import { PageHeader } from '../../components/ui/PageHeader';
import { AlertBanner } from '../../components/ui/AlertBanner';

type OngletKey = 'apercu' | 'resultats' | 'assiduite' | 'conduite'
  | 'sante' | 'communication' | 'rachats' | 'paiement';

const ONGLETS: { k: OngletKey; l: string; icon: React.ReactNode }[] = [
  { k: 'apercu', l: "Vue d'ensemble", icon: <User size={14} /> },
  { k: 'resultats', l: 'Résultats', icon: <Award size={14} /> },
  { k: 'assiduite', l: 'Assiduité', icon: <Clock size={14} /> },
  { k: 'conduite', l: 'Conduite', icon: <Shield size={14} /> },
  { k: 'sante', l: 'Santé', icon: <Heart size={14} /> },
  { k: 'communication', l: 'Communication', icon: <MessageSquare size={14} /> },
  { k: 'rachats', l: 'Rachats & Rattrapages', icon: <RefreshCw size={14} /> },
  { k: 'paiement', l: 'Paiement', icon: <CreditCard size={14} /> },
];

export default function BilanEtudiant() {
  const { matricule = '' } = useParams<{ matricule: string }>();
  const navigate = useNavigate();
  const [onglet, setOnglet] = useState<OngletKey>('apercu');
  const { data, isLoading, error, refetch } = useGetBilanQuery(matricule);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-amber-600" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <AlertBanner
          type="error"
          message={<>Impossible de charger le bilan de <span className="font-mono">{matricule}</span>.</>}
        />
      </div>
    );
  }

  const { identite, alertes } = data;

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4">
      {/* Header (unifié — refonte vague 3 lot 1) */}
      <PageHeader
        title={`${identite.nom} ${identite.prenoms}`}
        description={<span className="font-mono">{identite.matricule} · Bilan complet</span>}
        back={
          <button onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-gray-500"
            aria-label="Retour">
            <ArrowLeft size={18} />
          </button>
        }
        actions={
          <>
            <button onClick={() => refetch()}
              className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              title="Rafraîchir">
              <RefreshCw size={16} />
            </button>
            <button
              onClick={() => navigate(`/releves/notes?matricule=${identite.matricule}&type=provisoire`)}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded inline-flex items-center gap-1.5">
              <Printer size={13} /> Relevé
            </button>
            <button
              onClick={() => navigate(`/etudiants/${identite.id}/edit`)}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 text-xs font-semibold rounded inline-flex items-center gap-1.5">
              <Edit size={13} /> Modifier
            </button>
          </>
        }
      />

      {/* Bandeau photo + KPIs express */}
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="flex flex-col items-center gap-2 md:col-span-1">
          {identite.photo_url ? (
            <img src={identite.photo_url} alt="" className="w-24 h-24 rounded-full object-cover ring-2 ring-amber-500/30" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-slate-300 dark:bg-gray-700 flex items-center justify-center text-2xl font-bold text-white">
              {identite.nom.charAt(0)}{identite.prenoms.charAt(0)}
            </div>
          )}
          <div className="flex flex-wrap gap-1 justify-center">
            <Badge color={identite.actif ? 'emerald' : 'red'}>
              {identite.actif ? 'Actif' : 'Inactif'}
            </Badge>
            <Badge color="blue">{identite.niveau}</Badge>
          </div>
          <div className="text-xs text-slate-500 text-center">
            {identite.classe}{identite.option ? ` · ${identite.option}` : ''}
          </div>
        </div>

        {/* KPIs */}
        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          <KpiCard
            icon={<Award size={14} />}
            label="Moyenne S1 dernière"
            valeur={formatMoy(data.resultats?.semestres?.[0]?.moyenne)}
            couleur={colorMoyenne(data.resultats?.semestres?.[0]?.moyenne)}
          />
          <KpiCard
            icon={<Clock size={14} />}
            label="Taux présence 90j"
            valeur={data.assiduite.taux_presence != null ? `${data.assiduite.taux_presence}%` : '—'}
            couleur={
              data.assiduite.taux_presence == null ? 'slate'
              : data.assiduite.taux_presence >= 85 ? 'emerald'
              : data.assiduite.taux_presence >= 70 ? 'amber' : 'red'
            }
          />
          <KpiCard
            icon={<XCircle size={14} />}
            label="Absences 90j"
            valeur={data.assiduite.stats?.absents ?? 0}
            couleur={(data.assiduite.stats?.absents ?? 0) > 10 ? 'red' : (data.assiduite.stats?.absents ?? 0) > 5 ? 'amber' : 'emerald'}
          />
          <KpiCard
            icon={<Shield size={14} />}
            label="Fraudes actives"
            valeur={data.fraudes.length}
            couleur={data.fraudes.length > 0 ? 'red' : 'emerald'}
          />
        </div>
      </div>

      {/* Alertes */}
      {alertes.length > 0 && (
        <div className="space-y-2">
          {alertes.map((a, i) => (
            <div key={i} className={`p-3 rounded-lg border flex items-center gap-2 text-sm ${
              a.niveau === 'danger'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200'
            }`}>
              <AlertTriangle size={16} className="shrink-0" />
              <span className="font-semibold">{a.titre}</span>
              <span className="text-xs opacity-80 ml-auto">{a.action}</span>
            </div>
          ))}
        </div>
      )}

      {/* Onglets */}
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <div className="flex overflow-x-auto border-b border-slate-200 dark:border-gray-800">
          {ONGLETS.map(o => (
            <button key={o.k}
              onClick={() => setOnglet(o.k)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 flex items-center gap-1.5 whitespace-nowrap ${
                onglet === o.k
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-white'
              }`}>
              {o.icon} {o.l}
            </button>
          ))}
        </div>

        <div className="p-4">
          {onglet === 'apercu' && <SectionApercu data={data} />}
          {onglet === 'resultats' && <SectionResultats data={data} />}
          {onglet === 'assiduite' && <SectionAssiduite data={data} />}
          {onglet === 'conduite' && <SectionConduite data={data} />}
          {onglet === 'sante' && <SectionSante data={data} />}
          {onglet === 'communication' && <SectionCommunication data={data} />}
          {onglet === 'rachats' && <SectionRachats data={data} />}
          {onglet === 'paiement' && <SectionPaiement data={data} />}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sections
// ─────────────────────────────────────────────────────────────────────────────

function SectionApercu({ data }: { data: any }) {
  const id = data.identite;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <SectionTitle>Informations personnelles</SectionTitle>
        <InfoGrid>
          <InfoLine icon={<User size={12} />} label="Nom complet" value={`${id.nom} ${id.prenoms}`} />
          <InfoLine icon={<Hash size={12} />} label="Matricule" value={id.matricule} />
          <InfoLine icon={<User size={12} />} label="Sexe" value={id.sexe === 'M' ? 'Masculin' : id.sexe === 'F' ? 'Féminin' : '—'} />
          <InfoLine icon={<Calendar size={12} />} label="Date de naissance" value={id.date_naissance ? new Date(id.date_naissance).toLocaleDateString('fr-FR') : '—'} />
          <InfoLine icon={<MapPin size={12} />} label="Lieu de naissance" value={id.lieu_naissance || '—'} />
          <InfoLine icon={<Mail size={12} />} label="Email" value={id.email || '—'} />
          <InfoLine icon={<Phone size={12} />} label="Téléphone" value={id.telephone || '—'} />
        </InfoGrid>
      </div>
      <div>
        <SectionTitle>Cursus</SectionTitle>
        <InfoGrid>
          <InfoLine icon={<GraduationCap size={12} />} label="Niveau" value={id.niveau} />
          <InfoLine icon={<GraduationCap size={12} />} label="Classe" value={id.classe} />
          <InfoLine icon={<GraduationCap size={12} />} label="Option" value={id.option || '—'} />
          <InfoLine icon={<GraduationCap size={12} />} label="Parcours" value={id.parcours || '—'} />
          <InfoLine icon={<GraduationCap size={12} />} label="Pôle" value={id.pole || '—'} />
          <InfoLine icon={<Hash size={12} />} label="Type formation" value={id.type_formation || 'FI'} />
        </InfoGrid>

        {data.resultats?.top_matieres?.length > 0 && (
          <>
            <SectionTitle className="mt-4">Meilleures matières</SectionTitle>
            <div className="space-y-1">
              {data.resultats.top_matieres.map((m: any, i: number) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded text-xs">
                  <TrendingUp size={12} className="text-emerald-600" />
                  <span className="flex-1 truncate">{m.nom}</span>
                  <span className="font-bold text-emerald-700 dark:text-emerald-300">{formatMoy(m.moyenne)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {data.resultats?.matieres_a_ameliorer?.length > 0 && (
          <>
            <SectionTitle className="mt-4">À améliorer</SectionTitle>
            <div className="space-y-1">
              {data.resultats.matieres_a_ameliorer.map((m: any, i: number) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                  <TrendingDown size={12} className="text-red-600" />
                  <span className="flex-1 truncate">{m.nom}</span>
                  <span className="font-bold text-red-700 dark:text-red-300">{formatMoy(m.moyenne)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SectionResultats({ data }: { data: any }) {
  const semestres = data.resultats?.semestres || [];
  const ues = data.resultats?.ue_derniere || [];
  return (
    <div>
      <SectionTitle>Historique semestres</SectionTitle>
      {semestres.length === 0 ? (
        <Empty>Aucun résultat semestre disponible.</Empty>
      ) : (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 dark:bg-gray-800">
              <tr>
                <Th>Année</Th><Th>Sem</Th><Th className="text-right">Moyenne</Th>
                <Th className="text-right">Crédits</Th><Th className="text-right">Rang</Th><Th>Décision</Th>
              </tr>
            </thead>
            <tbody>
              {semestres.map((s: any, i: number) => (
                <tr key={i} className="border-b border-slate-100 dark:border-gray-800">
                  <Td>{s.annee || '—'}</Td>
                  <Td className="font-semibold">{s.semestre}</Td>
                  <Td className={`text-right font-bold text-base ${colorMoyenneText(s.moyenne)}`}>
                    {formatMoy(s.moyenne)}
                  </Td>
                  <Td className="text-right">{s.credits_valides}/{s.credits_totaux}</Td>
                  <Td className="text-right">
                    {s.rang ? (
                      <>{s.rang}{s.effectif_classe ? `/${s.effectif_classe}` : ''}</>
                    ) : '—'}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge color={s.valide ? 'emerald' : s.doit_rattraper ? 'amber' : 'red'}>
                        {s.decision}
                      </Badge>
                      {s.session_validation && (
                        <Badge color="blue">{s.session_validation === 'normale' ? '1ère sess.' : 'Rattr.'}</Badge>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SectionTitle>UE — dernier semestre</SectionTitle>
      {ues.length === 0 ? (
        <Empty>Aucune UE.</Empty>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {ues.map((u: any, i: number) => (
            <div key={i} className={`p-2 border rounded flex items-center gap-2 text-xs ${
              u.eliminatoire ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10' : 'border-slate-200 dark:border-gray-800'
            }`}>
              <span className="font-mono text-slate-500 shrink-0">{u.ue_code || '—'}</span>
              <span className="flex-1 truncate">{u.ue_libelle || '—'}</span>
              {u.eliminatoire && <Badge color="red">Élim.</Badge>}
              <span className="text-slate-400 text-[10px]">{u.credits} cr.</span>
              <span className={`font-bold w-12 text-right ${colorMoyenneText(u.moyenne)}`}>{formatMoy(u.moyenne)}</span>
              {u.valide ? <CheckCircle2 size={12} className="text-emerald-600" /> : <XCircle size={12} className="text-red-500" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionAssiduite({ data }: { data: any }) {
  const st = data.assiduite.stats || {};
  const pm = data.assiduite.par_matiere || [];
  const abs = data.assiduite.absences_recentes || [];
  return (
    <div>
      <SectionTitle>Statistiques 90 derniers jours</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <StatBox label="Pointages" v={st.total_pointages || 0} />
        <StatBox label="Présents" v={st.presents || 0} c="emerald" />
        <StatBox label="Absents" v={st.absents || 0} c="red" />
        <StatBox label="Retards" v={st.retards || 0} c="amber" />
        <StatBox label="Justifiés" v={st.justifies || 0} c="blue" />
      </div>

      <SectionTitle>Par matière</SectionTitle>
      {pm.length === 0 ? <Empty>Aucune donnée.</Empty> : (
        <div className="space-y-1 mb-4">
          {pm.map((m: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-40 truncate">{m.matiere}</span>
              <div className="flex-1 h-2 bg-slate-200 dark:bg-gray-800 rounded overflow-hidden">
                <div className={`h-full ${
                  m.taux_present >= 85 ? 'bg-emerald-500' :
                  m.taux_present >= 70 ? 'bg-amber-500' : 'bg-red-500'
                }`} style={{ width: `${m.taux_present}%` }} />
              </div>
              <span className="w-14 text-right font-bold">{m.taux_present}%</span>
              <span className="w-16 text-right text-slate-500">{m.absents}/{m.total} abs.</span>
            </div>
          ))}
        </div>
      )}

      <SectionTitle>Dernières absences ({abs.length})</SectionTitle>
      {abs.length === 0 ? <Empty>Aucune absence récente.</Empty> : (
        <div className="space-y-1 mb-4">
          {abs.map((a: any, i: number) => (
            <div key={i} className="p-2 bg-slate-50 dark:bg-gray-800/50 rounded text-xs">
              <div className="flex items-center gap-2">
                <span className="w-20 font-mono text-slate-500">{new Date(a.date).toLocaleDateString('fr-FR')}</span>
                {a.heure && <span className="text-slate-400 font-mono">{a.heure}</span>}
                <span className="flex-1 truncate">{a.matiere}</span>
                <span className="text-[10px] text-slate-500">{a.classe}</span>
                <Badge color={a.justifie ? 'blue' : 'red'}>
                  {a.justifie ? 'Justifiée' : 'Non justifiée'}
                </Badge>
                {a.justification_par_parent && !a.justifie && (
                  <Badge color="amber">À valider</Badge>
                )}
              </div>
              {a.motif && (
                <div className="mt-1 pl-20 text-slate-600 dark:text-gray-400 italic">
                  {a.motif}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {(data.assiduite.retards_recents || []).length > 0 && (
        <>
          <SectionTitle>Retards récents</SectionTitle>
          <div className="space-y-1">
            {data.assiduite.retards_recents.map((r: any, i: number) => (
              <div key={i} className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded flex items-center gap-2 text-xs">
                <span className="w-20 font-mono text-slate-500">{new Date(r.date).toLocaleDateString('fr-FR')}</span>
                <span className="flex-1 truncate">{r.matiere}</span>
                <span className="font-bold text-amber-700 dark:text-amber-300">+{r.minutes} min</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SectionConduite({ data }: { data: any }) {
  const entrees = data.conduite.entrees || [];
  const stats = data.conduite.stats || {};
  return (
    <div>
      {/* Score global */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatBox label="Positifs" v={stats.positifs || 0} c="emerald" />
        <StatBox label="Négatifs" v={stats.negatifs || 0} c="red" />
        <StatBox label="Score net" v={stats.score ?? 0} c={
          (stats.score ?? 0) > 0 ? 'emerald' : (stats.score ?? 0) < 0 ? 'red' : 'slate'
        } />
      </div>

      <SectionTitle>Observations (20 dernières)</SectionTitle>
      {entrees.length === 0 ? <Empty>Aucune observation.</Empty> : (
        <div className="space-y-2">
          {entrees.map((e: any) => (
            <div key={e.id} className={`p-3 rounded border ${
              e.type === 'positif'
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge color={e.type === 'positif' ? 'emerald' : 'red'}>
                  {e.type === 'positif' ? '+ Positif' : '− Négatif'}
                </Badge>
                <Badge color="blue">{e.categorie_display}</Badge>
                {e.poids > 1 && (
                  <Badge color={e.poids === 3 ? 'red' : 'amber'}>{e.poids_display}</Badge>
                )}
                <span className="text-xs text-slate-500 ml-auto">
                  {e.date_observation ? new Date(e.date_observation).toLocaleDateString('fr-FR') : ''}
                </span>
              </div>
              {e.commentaire && <div className="text-xs text-slate-700 dark:text-gray-300 whitespace-pre-wrap">{e.commentaire}</div>}
              {e.auteur_nom && <div className="text-[10px] text-slate-500 mt-1">
                — {e.auteur_nom} {e.auteur_role ? `(${e.auteur_role})` : ''}
              </div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionSante({ data }: { data: any }) {
  const s = data.sante;
  if (!s) return <Empty>Aucune fiche santé.</Empty>;
  const f = s.fiche;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <SectionTitle>Fiche santé</SectionTitle>
        {!f ? <Empty>Fiche non renseignée.</Empty> : (
          <InfoGrid>
            <InfoLine icon={<Heart size={12} />} label="Groupe sanguin" value={f.groupe_sanguin || '—'} />
            <InfoLine icon={<AlertTriangle size={12} />} label="Allergies" value={f.allergies || '—'} />
            <InfoLine icon={<Clock size={12} />} label="Traitements" value={f.traitements_en_cours || '—'} />
            <InfoLine icon={<FileText size={12} />} label="Antécédents" value={f.antecedents || '—'} />
            <InfoLine icon={<FileText size={12} />} label="Observations" value={f.observations || '—'} />
          </InfoGrid>
        )}
      </div>
      <div>
        <SectionTitle>Contacts urgence</SectionTitle>
        {(s.contacts_urgence || []).length === 0 ? <Empty>Aucun contact.</Empty> : (
          <div className="space-y-1">
            {s.contacts_urgence.map((c: any, i: number) => (
              <div key={i} className="p-2 border border-slate-200 dark:border-gray-800 rounded text-xs">
                <div className="font-semibold">{c.nom} <span className="text-slate-500 font-normal">({c.lien})</span></div>
                <div className="text-slate-500">📞 {c.telephone || '—'}</div>
              </div>
            ))}
          </div>
        )}
        <SectionTitle className="mt-4">Incidents récents</SectionTitle>
        {(s.incidents_recents || []).length === 0 ? <Empty>Aucun incident.</Empty> : (
          <div className="space-y-1">
            {s.incidents_recents.map((i: any, k: number) => (
              <div key={k} className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                <div className="flex items-center gap-2">
                  <Badge color="red">{i.gravite || i.type}</Badge>
                  <span className="text-slate-500 ml-auto">
                    {i.date ? new Date(i.date).toLocaleDateString('fr-FR') : ''}
                  </span>
                </div>
                {i.description && <div className="mt-1">{i.description}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionCommunication({ data }: { data: any }) {
  const mp = data.communication.messages_parents || [];
  const me = data.communication.messages_etudiant || [];
  const rec = data.reclamations || [];
  const fraudes = data.fraudes || [];

  return (
    <div className="space-y-6">
      {/* Réclamations — détail complet */}
      <div>
        <SectionTitle>Réclamations ({rec.length})</SectionTitle>
        {rec.length === 0 ? <Empty>Aucune réclamation.</Empty> : (
          <div className="space-y-2">
            {rec.map((r: any) => <ReclamationCard key={r.id} r={r} />)}
          </div>
        )}
      </div>

      {/* Fraudes détaillées */}
      {fraudes.length > 0 && (
        <div>
          <SectionTitle>Cas de fraude ({fraudes.length})</SectionTitle>
          <div className="space-y-2">
            {fraudes.map((f: any) => (
              <div key={f.id} className="p-3 border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-red-800 dark:text-red-300">{f.reference}</span>
                  <Badge color="red">{f.type_fraude}</Badge>
                  {f.gravite && <Badge color="amber">{f.gravite}</Badge>}
                  <Badge color="slate">{f.statut}</Badge>
                  <span className="text-slate-500 ml-auto">
                    {f.date ? new Date(f.date).toLocaleDateString('fr-FR') : ''}
                  </span>
                </div>
                {f.matiere_nom && (
                  <div className="mt-1 text-slate-700 dark:text-gray-300">
                    Matière : <span className="font-medium">{f.matiere_nom}</span>
                    {f.matiere_code && <span className="text-slate-500 font-mono ml-1">({f.matiere_code})</span>}
                  </div>
                )}
                {f.sanction && (
                  <div className="mt-1"><span className="text-slate-500">Sanction :</span> <span className="font-semibold">{f.sanction}</span></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages parents + étudiant en 2 colonnes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <SectionTitle>Messages parents ({mp.length})</SectionTitle>
          {mp.length === 0 ? <Empty>Aucun.</Empty> : (
            <div className="space-y-2">
              {mp.map((m: any) => (
                <div key={m.id} className="p-3 border border-slate-200 dark:border-gray-800 rounded text-xs">
                  <div className="flex items-center gap-1 flex-wrap">
                    <Badge color={m.auteur_type === 'parent' ? 'blue' : 'emerald'}>
                      {m.auteur_type === 'parent' ? '→ Parent' : '← École'}
                    </Badge>
                    <Badge color="slate">→ {m.destinataire}</Badge>
                    {!m.lu && <Badge color="amber">Non lu</Badge>}
                    <span className="text-slate-500 ml-auto">
                      {m.date_envoi ? new Date(m.date_envoi).toLocaleDateString('fr-FR') : ''}
                    </span>
                  </div>
                  {m.sujet && <div className="font-semibold mt-1">{m.sujet}</div>}
                  <div className="text-slate-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">{m.contenu}</div>
                  {m.auteur_nom && <div className="text-[10px] text-slate-500 mt-1">— {m.auteur_nom}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <SectionTitle>Messages étudiant ↔ scolarité ({me.length})</SectionTitle>
          {me.length === 0 ? <Empty>Aucun.</Empty> : (
            <div className="space-y-2">
              {me.map((m: any) => (
                <div key={m.id} className={`p-3 border rounded text-xs ${
                  m.important ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10'
                             : 'border-slate-200 dark:border-gray-800'
                }`}>
                  <div className="flex items-center gap-1 flex-wrap">
                    <Badge color={m.traite ? 'emerald' : 'amber'}>{m.statut}</Badge>
                    {m.important && <Badge color="red">⭐ Important</Badge>}
                    <span className="text-slate-500 ml-auto">
                      {m.date ? new Date(m.date).toLocaleDateString('fr-FR') : ''}
                    </span>
                  </div>
                  {m.sujet && <div className="font-semibold mt-1">{m.sujet}</div>}
                  <div className="text-slate-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">{m.message}</div>
                  {m.reponse && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-400 rounded">
                      <div className="text-[10px] text-blue-700 dark:text-blue-300 font-semibold">
                        Réponse scolarité{m.repondu_par_nom ? ` — ${m.repondu_par_nom}` : ''}
                      </div>
                      <div className="text-slate-700 dark:text-gray-300 mt-0.5 whitespace-pre-wrap">{m.reponse}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReclamationCard({ r }: { r: any }) {
  const [ouvert, setOuvert] = useState(false);
  const statutColor: 'blue' | 'amber' | 'emerald' | 'red' | 'slate' =
    r.statut_code === 'acceptee' ? 'emerald' :
    r.statut_code === 'rejetee' ? 'red' :
    r.statut_code === 'en_examen' ? 'amber' :
    r.statut_code === 'cloturee' ? 'slate' : 'blue';
  const dateEnvoi = r.date_creation ? new Date(r.date_creation).toLocaleString('fr-FR') : '—';
  return (
    <div className="border border-slate-200 dark:border-gray-800 rounded overflow-hidden">
      <button onClick={() => setOuvert(o => !o)}
        className="w-full p-3 flex items-start gap-2 text-left hover:bg-slate-50 dark:hover:bg-gray-800/50">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-slate-400 text-[10px]">#{r.id}</span>
            <Badge color={statutColor}>{r.statut}</Badge>
            <Badge color="slate">{r.type_reclamation}</Badge>
            {r.semestre && <Badge color="blue">{r.semestre}</Badge>}
            {r.nb_non_lus_admin > 0 && (
              <Badge color="red">{r.nb_non_lus_admin} non lu(s)</Badge>
            )}
            <span className="text-xs text-slate-500 ml-auto">{dateEnvoi}</span>
          </div>
          {r.matiere_nom && (
            <div className="mt-1 text-xs font-semibold">
              {r.matiere_nom}
              {r.note_actuelle != null && r.note_attendue != null && (
                <span className="ml-2 text-slate-500 font-normal">
                  Note actuelle: <span className="text-red-600 font-bold">{r.note_actuelle}</span>
                  → attendue: <span className="text-emerald-600 font-bold">{r.note_attendue}</span>
                </span>
              )}
            </div>
          )}
          <div className="text-xs text-slate-600 dark:text-gray-400 mt-1 line-clamp-2">{r.motif}</div>
        </div>
        <ChevronRight size={14} className={`text-slate-400 transition-transform ${ouvert ? 'rotate-90' : ''}`} />
      </button>
      {ouvert && (
        <div className="border-t border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900/50 p-3 space-y-2 text-xs">
          <div>
            <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Motif complet</div>
            <div className="whitespace-pre-wrap">{r.motif || '—'}</div>
          </div>
          {r.reponse_scolarite && (
            <div>
              <div className="text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-400 mb-1">
                Réponse scolarité
                {r.traite_par_nom ? ` — ${r.traite_par_nom}` : ''}
                {r.date_traitement ? ` — ${new Date(r.date_traitement).toLocaleDateString('fr-FR')}` : ''}
              </div>
              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 border-l-2 border-emerald-500 rounded whitespace-pre-wrap">
                {r.reponse_scolarite}
              </div>
            </div>
          )}
          <div className="text-[10px] text-slate-500 flex items-center gap-3">
            <span>💬 {r.nb_messages} message(s)</span>
            {r.derniere_activite && (
              <span>Dernière activité: {new Date(r.derniere_activite).toLocaleString('fr-FR')}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionRachats({ data }: { data: any }) {
  const rc = data.rachats || [];
  const rt = data.rattrapages || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <SectionTitle>Rachats appliqués ({rc.length})</SectionTitle>
        {rc.length === 0 ? <Empty>Aucun rachat.</Empty> : (
          <div className="space-y-2">
            {rc.map((r: any) => (
              <div key={r.id} className={`p-2 border rounded text-xs ${
                r.annulation
                  ? 'opacity-70 border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/40'
                  : 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge color={r.annulation ? 'slate' : 'emerald'}>{r.type_operation}</Badge>
                  {r.source_note && <Badge color="blue">{r.source_note}</Badge>}
                  <span className="text-slate-500 ml-auto text-[10px]">
                    {r.date ? new Date(r.date).toLocaleDateString('fr-FR') : ''}
                  </span>
                </div>
                <div className="font-semibold truncate">
                  {r.matiere}
                  {r.matiere_code && <span className="text-slate-400 font-mono ml-1 text-[10px]">({r.matiere_code})</span>}
                </div>
                <div className="text-slate-500 flex items-center gap-1 mt-0.5">
                  <span className={r.annulation ? '' : 'line-through'}>{formatMoy(r.note_avant)}</span>
                  <ChevronRight size={10} />
                  <span className={`font-bold ${r.annulation ? 'text-slate-600' : 'text-emerald-700 dark:text-emerald-300'}`}>
                    {formatMoy(r.note_apres)}
                  </span>
                </div>
                {r.justification && (
                  <div className="text-slate-600 dark:text-gray-400 mt-1 text-[11px] italic">{r.justification}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <SectionTitle>Rattrapages ({rt.length})</SectionTitle>
        {rt.length === 0 ? <Empty>Aucun.</Empty> : (
          <div className="space-y-1">
            {rt.map((r: any) => (
              <div key={r.id} className="p-2 border border-slate-200 dark:border-gray-800 rounded text-xs">
                <div className="font-semibold truncate">{r.matiere || '—'}</div>
                <div className="text-slate-500 mt-0.5 flex items-center gap-2">
                  {r.choix && <Badge color="amber">{r.choix}</Badge>}
                  {r.annee && <span>{r.annee}</span>}
                  {r.semestre && <span>· {r.semestre}</span>}
                </div>
                {r.date_choix && (
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {new Date(r.date_choix).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionPaiement({ data }: { data: any }) {
  const p = data.paiement;
  if (!p) return <Empty>Aucune donnée de paiement.</Empty>;
  return (
    <div>
      <SectionTitle>État paiement — {p.annee_academique}</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatusBox label="Inscription" ok={p.inscription} />
        <StatusBox label="MAR payé" ok={p.mar_paye} />
        <StatusBox label="Examen payé" ok={p.exam_paye} />
        <StatusBox label="Seuil à jour" ok={p.seuil_a_jour} />
      </div>
      {p.derniere_maj && (
        <div className="text-[10px] text-slate-500 mt-2">
          Dernière maj : {new Date(p.derniere_maj).toLocaleString('fr-FR')}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Composants utilitaires
// ─────────────────────────────────────────────────────────────────────────────

function SectionTitle({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <h3 className={`text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-gray-400 mb-2 ${className}`}>
      {children}
    </h3>
  );
}

function InfoGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 gap-1.5">{children}</div>;
}

function InfoLine({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-slate-400">{icon}</span>
      <span className="text-slate-500 w-32 shrink-0">{label}</span>
      <span className="font-medium text-slate-900 dark:text-white truncate">{value}</span>
    </div>
  );
}

function Badge({ children, color }: { children: React.ReactNode; color: 'emerald' | 'red' | 'amber' | 'blue' | 'slate' }) {
  const map = {
    emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    slate: 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300',
  };
  return <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase ${map[color]}`}>{children}</span>;
}

function KpiCard({ icon, label, valeur, couleur }: {
  icon: React.ReactNode; label: string;
  valeur: string | number; couleur: 'emerald' | 'red' | 'amber' | 'blue' | 'slate';
}) {
  const map = {
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    slate: 'bg-slate-50 dark:bg-gray-800 text-slate-700 dark:text-gray-300 border-slate-200 dark:border-gray-700',
  };
  return (
    <div className={`border rounded-lg p-3 ${map[couleur]}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase font-semibold">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold mt-1">{valeur}</div>
    </div>
  );
}

function StatBox({ label, v, c = 'slate' }: { label: string; v: number; c?: 'slate' | 'emerald' | 'red' | 'amber' | 'blue' }) {
  const map = {
    slate: 'text-slate-700 dark:text-gray-300',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
  };
  return (
    <div className="p-2 bg-slate-50 dark:bg-gray-800 rounded text-center">
      <div className={`text-xl font-bold ${map[c]}`}>{v}</div>
      <div className="text-[9px] uppercase text-slate-500">{label}</div>
    </div>
  );
}

function StatusBox({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className={`p-3 rounded border flex items-center gap-2 ${
      ok ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
         : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    }`}>
      {ok ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-red-600" />}
      <span className="text-xs font-semibold">{label}</span>
    </div>
  );
}

function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-2 py-1.5 text-left text-[10px] uppercase font-bold text-slate-500 ${className}`}>{children}</th>;
}
function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-2 py-1.5 ${className}`}>{children}</td>;
}
function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-center py-6 text-xs text-slate-500">{children}</div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatMoy(m: number | null | undefined): string {
  return m == null ? '—' : m.toFixed(2);
}
function colorMoyenne(m: number | null | undefined): 'emerald' | 'red' | 'amber' | 'slate' {
  if (m == null) return 'slate';
  if (m >= 12) return 'emerald';
  if (m >= 10) return 'amber';
  return 'red';
}
function colorMoyenneText(m: number | null | undefined): string {
  if (m == null) return 'text-slate-400';
  if (m >= 12) return 'text-emerald-600';
  if (m >= 10) return 'text-amber-600';
  return 'text-red-500';
}
